import type * as api from '@gitbook/api-client';

/**
 * Which runs a surface is interested in.
 *
 * `null` means "must be absent": a run targeting the space root does not belong under a page, and
 * a run targeting a change request does not belong on the live content.
 */
export interface ImportRunTargetFilter {
    space: string;
    changeRequest?: string | null | undefined;
    page?: string | null | undefined;
}

/**
 * A run started in this tab, before it is reported by the server.
 */
export interface StartedImportRun {
    id: string;
    /** Recorded with the run: the screen the user is on when it ends may belong to another one. */
    organizationId: string;
    target: api.ContentImportTarget;
    site: string | null;
    source: api.ContentImportSource;
    startedAt: string;
}

const ACTIVE_STATUSES: ReadonlyArray<api.ContentImportRun['status']> = ['pending', 'in-progress'];

/**
 * Shared empty result, so a surface with no import keeps a stable value and never re-renders on
 * account of one. The table of contents asks per page, so this is most of the calls.
 */
export const EMPTY_IMPORT_RUNS: api.ContentImportRun[] = [];

/**
 * How long a run that has ended is still worth reporting.
 *
 * Matches what the server embeds on a space or change request, so the surfaces reading a listing
 * instead do not end up showing a months-old import.
 */
export const IMPORT_RUN_MAX_AGE = 24 * 60 * 60 * 1000;

/**
 * How often the server is asked about a run whose channel has gone quiet.
 *
 * Only a fallback for a workflow that died without reporting: progress itself comes from the
 * channel, so this never has to be frequent.
 */
export const IMPORT_RUN_POLL_INTERVAL = 60_000;

/**
 * Whether the run can still make progress.
 */
export function isImportRunActive(run: api.ContentImportRun): boolean {
    return isImportRunStatusActive(run.status);
}

/**
 * Whether a run in this status can still make progress.
 */
export function isImportRunStatusActive(status: api.ContentImportRun['status']): boolean {
    return ACTIVE_STATUSES.includes(status);
}

/**
 * Whether the run is still going, or ended recently enough to be worth reporting.
 */
export function isRecentImportRun(run: api.ContentImportRun, now: number = Date.now()): boolean {
    if (isImportRunActive(run)) {
        return true;
    }

    const endedAt = run.completedAt ?? run.updatedAt ?? run.createdAt;
    return endedAt ? now - new Date(endedAt).getTime() < IMPORT_RUN_MAX_AGE : false;
}

/**
 * Whether the run reached an outcome it cannot move away from.
 */
export function isImportRunTerminal(run: api.ContentImportRun): boolean {
    return !isImportRunActive(run);
}

/**
 * Whether a run is one the filter asks for.
 */
export function checkImportRunMatchesTarget(
    run: api.ContentImportRun,
    filter: ImportRunTargetFilter
): boolean {
    const target = run.target;
    if (!target || target.space !== filter.space) {
        return false;
    }
    if (filter.changeRequest !== undefined) {
        if ((target.changeRequest ?? null) !== filter.changeRequest) {
            return false;
        }
    }
    if (filter.page !== undefined) {
        if ((target.page ?? null) !== filter.page) {
            return false;
        }
    }
    return true;
}

/**
 * Reconcile what the server reports about a run with what its channel is streaming.
 *
 * The channel carries the detail — the pages as they land — but it replays its last message and
 * never publishes one for a run whose workflow died, so it is the server that decides whether a run
 * is still going. Without that rule a dead import would stream `in-progress` forever.
 */
export function mergeImportRun(
    reported: api.ContentImportRun | undefined,
    live: api.ContentImportRun | undefined
): api.ContentImportRun | undefined {
    if (!reported) {
        return live;
    }
    if (!live) {
        return reported;
    }

    if (isImportRunTerminal(reported) && isImportRunActive(live)) {
        return reported;
    }

    // Both agree the run is going: the channel is ahead on the pages.
    return { ...reported, ...live };
}

/**
 * What a run started in this tab looks like until the server reports it.
 *
 * Lets the table of contents and the merge button react the moment the import is requested, rather
 * than once the change request has been refetched.
 */
export function createStartedImportRunStub(started: StartedImportRun): api.ContentImportRun {
    return {
        object: 'import-run',
        id: started.id,
        status: 'pending',
        source: started.source,
        target: started.target,
        ...(started.site ? { site: started.site } : {}),
        createdAt: started.startedAt,
        updatedAt: started.startedAt,
        pages: [],
    };
}

/**
 * Fold what the server was last asked about a run into what the resource reported.
 *
 * Only while the resource still believes the run is going: it is refetched on the mutations of a
 * run, so once it reports an outcome it is the fresher of the two. A run the resource does not
 * report at all is left out — a surface shows the runs its own resource carries.
 */
export function withPolledImportRuns(
    byId: Map<string, api.ContentImportRun>,
    polled: ReadonlyMap<string, api.ContentImportRun>
): api.ContentImportRun[] {
    for (const [id, run] of polled) {
        const reported = byId.get(id);
        if (reported && isImportRunActive(reported)) {
            byId.set(id, { ...reported, ...run });
        }
    }

    return Array.from(byId.values());
}

/**
 * Collate the runs a surface should show, newest first.
 */
export function mergeImportRuns(input: {
    reported: api.ContentImportRun[];
    live: ReadonlyMap<string, api.ContentImportRun>;
    started: StartedImportRun[];
}): api.ContentImportRun[] {
    const byId = new Map<string, api.ContentImportRun>();

    // The stub goes underneath, as a base the other two layer over. It is the only source that
    // knows where a run is going: the channel publishes a status and its pages, never a target, so
    // a run whose channel arrives before its resource does would otherwise have no target at all
    // and be filtered off every surface. Its own `pending` and empty pages always lose below.
    for (const started of input.started) {
        byId.set(started.id, createStartedImportRunStub(started));
    }
    for (const run of input.reported) {
        const stub = byId.get(run.id);
        byId.set(run.id, stub ? { ...stub, ...run } : run);
    }
    for (const [id, run] of input.live) {
        const merged = mergeImportRun(byId.get(id), run);
        if (merged) {
            byId.set(id, merged);
        }
    }

    return Array.from(byId.values()).sort(compareImportRunsByRecency);
}

/**
 * How an import reads in a badge or a list row.
 */
export function getImportRunDescriptor(run: api.ContentImportRun): {
    label: string;
    tooltip?: string;
} {
    switch (run.status) {
        case 'pending':
        case 'in-progress': {
            const { imported, total } = countImportedPages(run);
            if (total === 0) {
                return { label: 'Importing…' };
            }
            return {
                label: `Importing (${Math.round((imported * 100) / total)}%)`,
                tooltip: `${imported} out of ${total} pages imported`,
            };
        }
        case 'completed':
            return { label: 'Complete' };
        case 'failed':
            return { label: 'Import failed', tooltip: run.reason ?? undefined };
        case 'cancelled':
            return { label: 'Import cancelled' };
    }
}

/**
 * How many of the run's pages have been imported.
 *
 * Folders are left out: they are created as a side effect of the pages under them, so counting them
 * would report progress the user cannot see.
 */
export function countImportedPages(run: api.ContentImportRun): {
    imported: number;
    total: number;
} {
    return (run.pages ?? []).reduce(
        (acc, page) => {
            if (page.type === 'page') {
                acc.total++;
                if (page.status === 'complete') {
                    acc.imported++;
                }
            }
            return acc;
        },
        { imported: 0, total: 0 }
    );
}

/**
 * A short description of where the content came from.
 */
export function getImportRunSourceLabel(run: api.ContentImportRun): string {
    const source = run.source;
    if (!source) {
        return 'Import';
    }
    if (source.type === 'website') {
        return source.url;
    }
    const count = source.files.length;
    return count === 1 ? '1 file' : `${count} files`;
}

function compareImportRunsByRecency(a: api.ContentImportRun, b: api.ContentImportRun): number {
    return getImportRunTime(b) - getImportRunTime(a);
}

function getImportRunTime(run: api.ContentImportRun): number {
    const date = run.createdAt ?? run.startedAt;
    return date ? new Date(date).getTime() : 0;
}
